import type { OnViewableItemsChangedInfo } from '@legendapp/list/react-native';
import { DateTime } from 'luxon';
import { type ComponentProps, useEffect, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import eventStatus from '@/utils/eventStatus';
import pluralize from '@/utils/pluralize';
import type Icon from '../Unistyles/Icon';

export const EVENT_LIMIT = 10;

export const UseEventListFragment = graphql(/* GraphQL */ `
  fragment UseEventList on event {
    id
    datetime
  }
`);

type EventListDatetimeItem = {
  datetime?: string | null;
};

function getEventDateTime(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed : null;
}

function getEventTimestamp(value: string | null | undefined) {
  return getEventDateTime(value)?.toMillis() ?? Number.POSITIVE_INFINITY;
}

export function getEventListInitialIndex({
  events,
  divideUpcoming,
  timestamp,
}: {
  events: readonly EventListDatetimeItem[];
  divideUpcoming?: boolean;
  timestamp: number;
}) {
  if (!divideUpcoming) {
    return 0;
  }

  let nextEventIndex = -1;
  let nextEventTimestamp = Number.POSITIVE_INFINITY;
  for (const [index, event] of events.entries()) {
    const eventTimestamp = getEventTimestamp(event.datetime);
    if (
      Number.isFinite(eventTimestamp) &&
      eventTimestamp >= timestamp &&
      eventTimestamp < nextEventTimestamp
    ) {
      nextEventTimestamp = eventTimestamp;
      nextEventIndex = index;
    }
  }

  return nextEventIndex > -1 ? nextEventIndex : Math.max(events.length - 1, 0);
}

export function isNextUpcomingEventListItem({
  data,
  index,
  nowMs,
}: {
  data: readonly EventListDatetimeItem[];
  index: number;
  nowMs?: number;
}) {
  const now =
    typeof nowMs === 'number' ? DateTime.fromMillis(nowMs) : DateTime.local();
  const nowTimestamp = now.toMillis();
  const itemDateTime = getEventDateTime(data[index]?.datetime);
  let nextEventDateTime: DateTime | null = null;
  let nextEventTimestamp = Number.POSITIVE_INFINITY;

  for (const event of data) {
    const eventDateTime = getEventDateTime(event.datetime);
    const eventTimestamp =
      eventDateTime?.toMillis() ?? Number.POSITIVE_INFINITY;

    if (
      Number.isFinite(eventTimestamp) &&
      eventTimestamp >= nowTimestamp &&
      eventTimestamp < nextEventTimestamp
    ) {
      nextEventDateTime = eventDateTime;
      nextEventTimestamp = eventTimestamp;
    }
  }

  return (
    itemDateTime !== null &&
    nextEventDateTime !== null &&
    itemDateTime.toMillis() >= nowTimestamp &&
    itemDateTime.hasSame(nextEventDateTime, 'day')
  );
}

export function useEventList({
  events,
  divideUpcoming,
  initialDate,
  fetching,
}: {
  events: readonly FragmentOf<typeof UseEventListFragment>[];
  divideUpcoming?: boolean;
  initialDate?: string;
  fetching: boolean;
}) {
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const hasStartedFetchingRef = useRef(fetching);
  useEffect(() => {
    if (fetching) {
      hasStartedFetchingRef.current = true;
      return;
    }

    if (hasStartedFetchingRef.current) {
      setHasFetchedOnce(true);
    }
  }, [fetching]);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(
    null,
  );
  const [hasLayout, setHasLayout] = useState(false);
  const shouldRender =
    hasLayout && (hasFetchedOnce || (!fetching && events.length > 0));
  const nowTimestamp = DateTime.local().toMillis();
  const items = events.map((fragment) =>
    readFragment(UseEventListFragment, fragment),
  );
  const initialScrollIndex = getEventListInitialIndex({
    events: items,
    divideUpcoming,
    timestamp: getEventDateTime(initialDate)?.toMillis() ?? nowTimestamp,
  });
  const nextEventIndex = getEventListInitialIndex({
    events: items,
    divideUpcoming,
    timestamp: nowTimestamp,
  });
  const onLayout = (args: LayoutChangeEvent) => {
    if (args.nativeEvent.layout.height > 0) {
      setHasLayout(true);
    }
  };
  const onViewableItemsChanged = (
    info: OnViewableItemsChangedInfo<unknown>,
  ) => {
    if (info.viewableItems.length === 0) {
      return;
    }

    const firstVisibleIndex = info.viewableItems[0]?.index ?? 0;
    const lastVisibleIndex =
      info.viewableItems[info.viewableItems.length - 1]?.index ?? 0;

    if (nextEventIndex < firstVisibleIndex) {
      setScrollDirection('up');
    } else if (nextEventIndex > lastVisibleIndex) {
      setScrollDirection('down');
    } else {
      setScrollDirection(null);
    }
  };
  const keyExtractor = (item: { id: string }) => item.id;
  return {
    initialScrollIndex,
    shouldRender,
    container: {
      onLayout,
    },
    scrollButton: {
      scrollDirection,
    },
    list: {
      onViewableItemsChanged,
      keyExtractor,
    },
  };
}

export function getRelativeTimeInfo({
  status,
  hasClock,
  datetime,
  nowMs,
  isNextUpcomingEvent,
}: {
  status:
    | ReturnType<typeof graphql.scalar<'event_status_enum'>>
    | null
    | undefined;
  hasClock: boolean;
  datetime: string | null | undefined;
  nowMs?: number;
  isNextUpcomingEvent?: boolean;
}): {
  icon: ComponentProps<typeof Icon>['name'] | null;
  variant: 'typographySecondary' | 'warning' | 'info';
  title: string;
  highlight: boolean;
} | null {
  const now =
    typeof nowMs === 'number' ? DateTime.fromMillis(nowMs) : DateTime.local();
  const dt = DateTime.fromISO(datetime || '');
  const hoursUntil = dt.diff(now, 'hours').hours;
  const daysUntil = dt.endOf('day').diff(now, 'days').days;
  if (status) {
    return {
      icon: null,
      variant: 'typographySecondary',
      title: eventStatus[status],
      highlight: false,
    };
  }
  if (hasClock) {
    return {
      icon: 'radio',
      variant: 'warning',
      title: 'Live',
      highlight: true,
    };
  }
  if (hoursUntil < 0) {
    return {
      icon: null,
      variant: 'typographySecondary',
      title: '',
      highlight: false,
    };
  }
  if (hoursUntil < 1 && hoursUntil > 0) {
    return {
      icon: 'timer-outline',
      variant: 'warning',
      title: 'Starting Soon',
      highlight: true,
    };
  }
  if (hoursUntil > 6 && hoursUntil < 24) {
    const hours = Math.floor(hoursUntil);
    return {
      icon: 'timer-outline',
      variant: 'warning',
      title: `${hours} Hours`,
      highlight: true,
    };
  }
  if (hoursUntil >= 1 && hoursUntil < 6) {
    const totalMinutes = Math.max(
      0,
      Math.ceil((dt.toMillis() - now.toMillis()) / 60000),
    );
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return {
      icon: 'timer-outline',
      variant: 'warning',
      title: `${hours}H ${minutes}M`,
      highlight: true,
    };
  }
  if (daysUntil >= 1 && (daysUntil < 7 || isNextUpcomingEvent)) {
    const days = Math.floor(daysUntil);
    return {
      icon: 'timer-outline',
      variant: 'info',
      title: `${days} ${pluralize('Day', days)}`,
      highlight: true,
    };
  }
  return null;
}
